import React from 'react';
import { Button, Card, Col, Dropdown, Menu, Row, Tag } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useTranslation } from '../../i18n/useTranslation';

// svgs
import { ReactComponent as IconMoreVert } from '../../images/icon-more-vert.svg';
import { ReactComponent as IconAccTree } from '../../images/icon-account-tree.svg';
import { ReactComponent as IconMetaExplorer } from '../../images/icon-meta-explorer.svg';

// components, keys, and utils
import { BrowseDataProductRelatedEntityType, DataDataProductTable } from './BrowseDataProductTableType';
import CustomTooltip from '../entity/shared/components/common/CustomTooltip';
import { ModalKeys } from './BrowseDataProductModalKeys';
import EntityRegistry from '../entity/EntityRegistry';
import { CustomAvatar } from '../shared/avatar';

// generated
import { EntityType } from '../../types.generated';
import { AutoTooltip } from '../entity/shared/components/common/AutoTooltip';

interface Props {
    record: DataDataProductTable;
    isDropDownVisible: boolean;
    index: number;
    entityRegistry: EntityRegistry;
    onModalOpen: (key: string, dataProductUrn?: string, defaultValues?: BrowseDataProductRelatedEntityType[]) => void;
    setDropdownVisible: (value: number) => void;
}

const StandardBtn = styled(Button)`
    &&.ant-btn.icon-button {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2px !important;
    }

    svg {
        height: 20px !important;
        width: 20px !important;
    }
`;

const BrowseDataProductTableExplorer = ({
    record,
    isDropDownVisible,
    entityRegistry,
    onModalOpen,
    setDropdownVisible,
    index,
}: Props) => {
    const { t } = useTranslation();
    const { dataProduct, domain, glossaryTerms, tags, assets, owner } = record; // Correct way to get values
    const url = entityRegistry.getEntityUrl(EntityType.DataProduct, record.dataProduct.urn);

    const menuItems = [
        {
            label: t('Delete'),
            onClick: () => onModalOpen(ModalKeys.DELETE, dataProduct.urn),
        },
        {
            label: t('Update Domain'),
            onClick: () => onModalOpen(ModalKeys.UPDATE_DOMAIN, dataProduct.urn, [domain]),
        },
        {
            label: t('Update Owners'),
            onClick: () => onModalOpen(ModalKeys.UPDATE_OWNERS, dataProduct.urn, owner),
        },
        {
            label: t('Update Tags'),
            onClick: () => onModalOpen(ModalKeys.UPDATE_TAGS, dataProduct.urn, tags),
        },
        {
            label: t('Update Terms'),
            onClick: () => onModalOpen(ModalKeys.UPDATE_TERMS, dataProduct.urn, glossaryTerms),
        },
    ];
    return (
        <Row gutter={8} wrap={false} align="middle" justify="end" className="mx-0">
            <Col flex="none">
                <Dropdown
                    trigger={['click']}
                    visible={isDropDownVisible}
                    placement="bottomRight"
                    overlay={
                        <Card className="f-metadata-explorer-card">
                            <Row wrap={false} gutter={8} align="middle">
                                <Col flex="auto">
                                    <AutoTooltip className="f-metadata-title">{dataProduct.name}</AutoTooltip>
                                </Col>
                                <Col flex="none">
                                    <Button
                                        onClick={() => setDropdownVisible(-1)}
                                        type="text"
                                        icon={<CloseOutlined />}
                                        className="f-btn-action"
                                        title={t('Close Metadata Explorer')}
                                        aria-label={t('Close Metadata Explorer')}
                                    />
                                </Col>
                            </Row>
                            <div className="f-text-medium-content f-color-dark-black-s70 mb-2">
                                <AutoTooltip showTitle>
                                    {record.domain.path ? `${record.domain.path} / ` : ''} {record.domain.name}
                                </AutoTooltip>
                            </div>
                            <Button href={url} block type="primary">
                                <IconAccTree />
                                <span>{t('Explorer')}</span>
                            </Button>

                            <div className="f-metadata-subheader mt-3 mb-2">{t('Details')}</div>

                            <Row gutter={4} align="top">
                                <Col span={10}>
                                    <div className="f-metadata-label">{t('Domain')}</div>
                                </Col>
                                <Col span={14}>
                                    <AutoTooltip className="f-metadata-value">{domain.name}</AutoTooltip>
                                </Col>
                            </Row>
                            <Row gutter={4} align="middle">
                                <Col span={10}>
                                    <div className="f-metadata-label">{t('Glossary Terms')}</div>
                                </Col>
                                <Col flex="none">
                                    {glossaryTerms[0] ? (
                                        <Tag className="label-tag grey-tag-light ma-0">{glossaryTerms[0]?.name}</Tag>
                                    ) : (
                                        <span>-</span>
                                    )}
                                </Col>
                                {glossaryTerms.length > 1 && (
                                    <Col flex="none">
                                        <CustomTooltip items={glossaryTerms.map((item) => item.name)} />
                                    </Col>
                                )}
                            </Row>
                            <Row gutter={4} align="middle">
                                <Col span={10}>
                                    <div className="f-metadata-label">{t('Tags')}</div>
                                </Col>
                                <Col flex="none">
                                    {tags[0] ? (
                                        <Tag className="label-tag grey-tag-light ma-0">{tags[0]?.name}</Tag>
                                    ) : (
                                        <span>-</span>
                                    )}
                                </Col>
                                {tags.length > 1 && (
                                    <Col flex="none">
                                        <CustomTooltip items={tags.map((item) => item.name)} />
                                    </Col>
                                )}
                            </Row>
                            <Row gutter={4} align="middle">
                                <Col span={10}>
                                    <div className="f-metadata-label">{t('Assets')}</div>
                                </Col>
                                <Col span={14}>
                                    <div className="f-left-border-highlight f-height-auto py-0">{assets ?? '-'}</div>
                                </Col>
                            </Row>
                            <Row gutter={4} align="middle">
                                <Col span={10}>
                                    <div className="f-metadata-label">{t('Owners')}</div>
                                </Col>
                                <Col flex="none">
                                    {owner[0] ? <CustomAvatar name={owner[0]?.name} size={24} /> : <span>-</span>}
                                </Col>
                                {owner.length > 1 && (
                                    <Col flex="none">
                                        <CustomTooltip items={owner.map((item) => item.name)} />
                                    </Col>
                                )}
                            </Row>
                        </Card>
                    }
                >
                    <StandardBtn
                        onClick={() => {
                            setDropdownVisible(index);
                        }}
                        type="text"
                        icon={<IconMetaExplorer />}
                        className="icon-button ma-0"
                        title={t('Show Metadata Explorer')}
                        aria-label={t('Show Metadata Explorer')}
                    />
                </Dropdown>
            </Col>
            <Col flex="none">
                <Dropdown
                    trigger={['click']}
                    placement="bottomRight"
                    overlay={
                        <Menu className="f-dropdown-small">
                            {menuItems.map((item) => (
                                <Menu.Item onClick={item.onClick}>{item.label}</Menu.Item>
                            ))}
                        </Menu>
                    }
                >
                    <Button
                        type="text"
                        title={t('More Actions')}
                        icon={<IconMoreVert />}
                        aria-label={t('More Actions')}
                        className="icon-button ma-0"
                    />
                </Dropdown>
            </Col>
        </Row>
    );
};

export default BrowseDataProductTableExplorer;
